<template>
  <v-card class="pa-5">
    <v-container>
      <div>
        <v-col>
          <v-chip
            :color="getStatusColors(project.status).chip"
            :text-color="getStatusColors(project.status).text"
            dark>
            {{ initialStatus }}
          </v-chip>
          <v-menu v-model="statusMenuOpen" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="hasPermission('task.update_status')"
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="status in statusOptions"
                :key="status.id"
                @click="selectStatus(status.label)"
              >
                <v-list-item-content>{{ status.label }}</v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
        <v-col>
          <div>
            <v-list-item class="sticky-header">
              <v-list-item-content>
                <span class="text-h6 font-weight-bold text-black" v-if="multiUser"
                  >Task Members</span
                >
                <span class="text-h6 font-weight-bold text-black" v-else
                  >Task Member</span
                >
              </v-list-item-content>
            </v-list-item>
            <v-divider class="border-opacity-25"></v-divider>

            <v-list class="custom-list-style">
              <v-list-item-group
                v-for="(item, index) in project.members"
                :key="index"
                active-class="bg-white"
              >
                <v-list-item v-if="item.type !== 'divider'" three-line>
                  <v-list-item-avatar>
                    <v-avatar>
                      <img
                        v-if="item.profile_image"
                        :src="item.profile_image"
                      />
                      <img v-else :src="defaultImage" />
                    </v-avatar>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title
                      v-html="item.name"
                      class="font-weight-bold text-black"
                    ></v-list-item-title>
                    <v-list-item-subtitle
                     v-html="item.employer.speciality.name"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon
                      v-if="hasPermission('project.delete_member')"
                      @click="deleteMember(item.uuid)"
                      class="delete-icon"
                      color="red"
                      >mdi-delete</v-icon
                    >
                  </v-list-item-action>
                </v-list-item>

                <v-divider
                  v-if="index < project.members.length - 1"
                ></v-divider>
              </v-list-item-group>
            </v-list>

            <v-list-item class="sticky-footer">
              <v-list-item-content class="d-flex align-center justify-center">
                <v-btn
                  v-if="hasPermission('project.add_member') && multiUser"
                  color="#00B3FF"
                  @click="openAddMemberDialog"
                  class="white--text"
                  depressed
                  >Add Member</v-btn
                >
              </v-list-item-content>
            </v-list-item>
            <v-dialog v-model="addMemberDialog" persistent max-width="600px">
              <v-card class="scroll-disabled pa-5">
                <v-form
                  ref="addMemberForm"
                  @submit.prevent="addMembersToProject"
                >
                  <v-card-title>
                    <v-row align="center" justify="space-between">
                      <v-col cols="auto">
                        <span class="text-h6 font-weight-bold"
                          >Add Members to Task</span
                        >
                      </v-col>
                      <v-col cols="auto">
                        <v-btn
                          small
                          fab
                          depressed
                          class="mr-2 white--text"
                          color="blue"
                          sole
                          type="submit"
                        >
                          <v-icon>mdi-check</v-icon>
                        </v-btn>
                        <v-btn
                          small
                          fab
                          depressed
                          class="mr-2 white--text"
                          color="gray"
                          sole
                          @click="close"
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-autocomplete
                      v-model="selectedMembers"
                      :items="filteredMembers"
                      label="Select Members"
                      multiple
                      chips
                      deletable-chips
                      :item-text="formatMemberText"
                      item-value="uuid"
                      clearable
                    ></v-autocomplete>
                  </v-card-text>
                </v-form>
              </v-card>
            </v-dialog>
          </div>
        </v-col>
      </div>
    </v-container>
  </v-card>
</template>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  background-color: white;
}

.text-black {
  color: #000000;
}

.custom-list-style {
  height: 320px;
  overflow-y: auto;
  position: relative;
}

.scroll-disabled {
  overflow-y: hidden;
  overflow-x: hidden;
}
</style>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import { getStatusColors } from '@/utility/statusColors.js'; 

export default {
  props: {
    initialStatus: String,
    project: Object,
    projectMembersArray: Array,
    multiUser: Boolean,
  },
  data() {
    return {
      defaultImage: require("@/assets/img/avatar.svg"),
      selectedStatus: this.initialStatus || "",
      statusMenuOpen: false,
      statusOptions: [
        { id: 1, label: "IN-PROGRESS" },
        { id: 2, label: "COMPLETED" },
        { id: 3, label: "HOLD" },
        { id: 4, label: "PENDING" },
      ],
      members: [],
      addMemberDialog: false,
      selectedMembers: [],
    };
  },
  watch: {
  'projectMembersArray': {
    handler(newVal) {
      this.members = newVal || []; 
    },
    immediate: true
  }
},
  computed: {
    filteredMembers() {
      return this.members.filter((member) => !this.isMemberInProject(member));
    },
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  methods: {
    getStatusColors(status) {
      return getStatusColors(status);
    },
    selectStatus(status) {
      this.selectedStatus = status;
      this.statusMenuOpen = false;
      this.$emit("update-status", status);
    },

    openAddMemberDialog() {
      this.addMemberDialog = true;
    },
    addMembersToProject() {
      if (this.selectedMembers.length === 0) {
        this.addMemberDialog = true;
        return;
      }

      this.$emit("members-add", this.selectedMembers);
      this.close();
    },

    isMemberInProject(member) {
      return (
        this.project.members &&
        this.project.members.some(
          (projectMember) => projectMember.uuid === member.uuid
        )
      );
    },
    deleteMember(member) {
      this.$emit("members-delete", member);
    },
    close() {
      this.addMemberDialog = false;
      this.selectedMembers = [];
    },
    formatMemberText(member) {
      if (member.employer && member.employer.speciality) {
        return `${member.employer.full_name} - ${member.employer.speciality.name}`;
      } else {
        return member.name; 
      }
    },
  },
};
</script>
